import { SwapMode } from '../../common/types';
import { BASE_URL, CHAIN_ID_TO_NETWORKS, DEFAULT_FEE, DEFAULT_INTEGRATORS_CONFIG, DEFAULT_OPTIONS, DEFAULT_SLIPPAGE, MAX_AMOUNT_USD, MIN_AMOUNT_USD, NETWORK_TO_CHAIN_ID, SVC, X_CHAIN_ETH_MIN_AMOUNT_USD, } from './constants';
import { areAmountsValid, deconstructPairId, getGeolocationKey, memoizeWithTimeout, } from '../../utils';
import decodeTransaction from './utils/decode-transaction';
import { ETH_NULL_ADDRESS, ZERO_ALLOWANCE_ASSETS } from '../../constants';
import { APPROVAL_GAS_LIMIT, EXACT_OUT_ALLOWANCE_TRESHOLD, QUOTE_CACHE_TTL } from '../constants';
import { parseFetchivalError, serializeQuoteKey } from '../utils';
import { ApproveTokensError, CreateOrderError, CreateTxError, ExchangeError, GetAllowanceError, OrderValidationError, SendTxError, } from '../../errors';
class LiFi {
    name = 'li-fi';
    _baseUrl;
    _assetsService;
    _ordersService;
    _pricingService;
    _fetchival;
    _fee;
    _apiKeys;
    _integrators;
    _options;
    _tracer;
    _apiKey;
    _integrator;
    _cachedQuotes = new Map();
    constructor({ apiKeys, assetsService, baseUrl = BASE_URL, options = Object.create(null), ordersService, pricingService, fetchival, integrators = DEFAULT_INTEGRATORS_CONFIG, feePercentage = DEFAULT_FEE, tracer, }) {
        this._baseUrl = new URL(baseUrl);
        this._assetsService = assetsService;
        this._options = { ...DEFAULT_OPTIONS, ...options };
        this._ordersService = ordersService;
        this._pricingService = pricingService;
        this._fee = feePercentage;
        this._apiKeys = apiKeys;
        this._integrators = integrators;
        this._tracer = tracer;
        this._fetchival = fetchival;
        this._integrator = this._integrators.default;
        this._apiKey = this._apiKeys?.default;
    }
    async getSupportedFromAssetIds() {
        const tokens = await this._getTokens();
        return new Set(tokens.keys());
    }
    async getSupportedToAssetIds(from) {
        const tokens = await this.getSupportedFromAssetIds();
        return tokens.has(from) ? tokens : new Set();
    }
    async isPairSupported(pairId, opts) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            return false;
        }
        const fromNetwork = fromAsset.network;
        const toNetwork = toAsset.network;
        const shouldIncludeBridge = opts?.includePartners?.length;
        if (!shouldIncludeBridge && fromNetwork !== toNetwork) {
            return false;
        }
        const tokens = await this._getTokens();
        const allLiFiTokens = [...tokens.values()];
        const isFromBaseAsset = fromAsset[fromNetwork] === undefined;
        const isToBaseAsset = toAsset[toNetwork] === undefined;
        const isFromAvailable = allLiFiTokens.some((token) => {
            const tokenNetwork = CHAIN_ID_TO_NETWORKS.get(token.chainId);
            if (isFromBaseAsset) {
                return tokenNetwork === fromAsset.network && token.address === ETH_NULL_ADDRESS;
            }
            const tokenAddress = token.address.toLowerCase();
            const assetAddress = Object.values(fromAsset[fromNetwork] || {})[0]?.toLowerCase();
            return assetAddress === tokenAddress;
        });
        const isToAvailable = allLiFiTokens.some((token) => {
            const tokenNetwork = CHAIN_ID_TO_NETWORKS.get(token.chainId);
            if (isToBaseAsset) {
                return tokenNetwork === toAsset.network && token.address === ETH_NULL_ADDRESS;
            }
            const tokenAddress = token.address.toLowerCase();
            const assetAddress = Object.values(toAsset[toNetwork] || {})[0]?.toLowerCase();
            return assetAddress === tokenAddress;
        });
        if (!isFromAvailable || !isToAvailable) {
            return false;
        }
        const isAvailableViaDex = fromAsset.network === toAsset.network;
        if (shouldIncludeBridge) {
            const supportedChainByBridge = await this._fetchSupportedChainsByBridge(opts.includePartners);
            const isAvailableViaBridge = [...supportedChainByBridge.values()].some(([fromChainName, toChainName]) => (fromChainName === fromAsset.network && toChainName === toAsset.network) ||
                (fromChainName === toAsset.network && toChainName === fromAsset.network));
            return isAvailableViaDex || isAvailableViaBridge;
        }
        return isAvailableViaDex;
    }
    async getQuote(params) {
        const { pairId, amount, fromAddress, slippage, swapMode = SwapMode.ExactIn } = params;
        const quoteCacheKey = serializeQuoteKey({ pairId, amount, fromAddress, slippage, swapMode });
        const cachedQuote = this._cachedQuotes.get(quoteCacheKey);
        if (!cachedQuote || cachedQuote.expiry < Date.now()) {
            const quote = await this._getQuote(params);
            this._cachedQuotes.set(quoteCacheKey, { quote, expiry: Date.now() + QUOTE_CACHE_TTL });
        }
        return this._cachedQuotes.get(quoteCacheKey).quote;
    }
    async _getQuote(params) {
        const { pairId, amount, fromAddress, toAddress, includePartners, slippage = DEFAULT_SLIPPAGE, swapMode = SwapMode.ExactIn, getAllowance, } = params;
        const { fromAsset, fromToken, toAsset, toToken } = await this._getAssetsAndTokensFromPairId(pairId);
        const [quote, limits] = await Promise.all([
            this._getQuoteFromLifi({
                amount,
                fromAddress,
                fromAsset,
                fromToken,
                slippage,
                toAsset,
                toToken,
                includePartners,
                toAddress,
                swapMode,
            }),
            this.getLimits({ pairId }),
        ]);
        const limit = swapMode === SwapMode.ExactIn ? limits[0]?.ExactIn : limits[0]?.ExactOut;
        if (!limit || amount.lt(limit.min) || amount.gt(limit.max)) {
            const { value: minValue = 'UNDEF_VAL' } = limit?.min || {};
            const { value: maxValue = 'UNDEF_VAL' } = limit?.max || {};
            const err = new Error(`No rate found for ${amount.toDefaultString()} (min: ${minValue} - max: ${maxValue})`);
            err.name = 'NO_RATE_AVAILABLE';
            throw err;
        }
        const allowance = await getAllowance({
            approvalAddress: quote.estimate.approvalAddress,
            assetId: fromAsset.id,
        });
        const fromAmount = swapMode === SwapMode.ExactIn
            ? amount
            : fromAsset.currency.baseUnit(quote.estimate.fromAmount);
        const shouldApproveTokens = fromToken.address !== ETH_NULL_ADDRESS && allowance.lt(fromAmount);
        const shouldResetApprovalTokens = allowance.isPositive && ZERO_ALLOWANCE_ASSETS.includes(fromAsset.id);
        const approvalGasLimit = shouldApproveTokens
            ? shouldResetApprovalTokens
                ? APPROVAL_GAS_LIMIT * 2
                : APPROVAL_GAS_LIMIT
            : 0;
        const txGasLimit = this._estimateGasLimit(quote.estimate.gasCosts);
        const quoteAmount = swapMode === SwapMode.ExactIn
            ? toAsset.currency.baseUnit(quote.estimate.toAmount)
            : fromAsset.currency.baseUnit(quote.estimate.fromAmount);
        const rate = swapMode === SwapMode.ExactIn
            ? quoteAmount.div(amount.toDefaultString()).toDefaultString()
            : amount.div(quoteAmount.toDefaultString()).toDefaultString();
        return {
            providerName: this.name,
            rateId: quote.id,
            amount: quoteAmount,
            minerFee: toAsset.currency.defaultUnit(0),
            rate,
            exchangeFeeData: {
                feeOpts: {
                    gasLimit: txGasLimit + approvalGasLimit,
                },
            },
            exchangeTxData: {
                transactionRequest: quote.transactionRequest,
                approvalAddress: quote.estimate.approvalAddress,
            },
        };
    }
    async prepareExchange(params) {
        try {
            const traceMessage = this._traceMessageScope('prepareExchange');
            const { fromAddress, fromAmount, pairId, slippage = DEFAULT_SLIPPAGE, toAddress, toAmount, isSendAll, anonymousId, referralCode, swapMode = SwapMode.ExactIn, quote, createTx, onOrderIdGenerated, } = params;
            const { fromAsset } = await this._getAssetsAndTokensFromPairId(pairId);
            traceMessage('Creating order in the exchange-server');
            this._tracer?.traceTime('LogDexOrder');
            const orderPayload = {
                fromAmount: fromAmount.toDefaultString({ unit: false }),
                fromAddress,
                toAddress,
                toAmount: toAmount.toDefaultString({ unit: false }),
                pairId,
                slippage,
                svc: SVC,
                referralCode,
                swapMode,
            };
            const order = await this._ordersService
                .insertDex(orderPayload, { anonymousId })
                .catch(async (err) => {
                const errorMessage = await parseFetchivalError(err);
                throw new CreateOrderError({
                    message: errorMessage,
                    details: orderPayload,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('LogDexOrder'));
            if (typeof onOrderIdGenerated === 'function') {
                onOrderIdGenerated(order.id);
            }
            const { transactionRequest, approvalAddress } = quote.exchangeTxData;
            traceMessage('Decoding LiFi transaction');
            const lifiTransaction = decodeTransaction(transactionRequest.data, fromAddress);
            traceMessage('Checking order validity');
            this._tracer?.traceTime('CheckOrderIsValid');
            const orderCheckPromise = this._checkOrderIsValid({
                lifiTransaction,
                order: { ...order, slippage, swapMode },
            })
                .catch((err) => {
                throw new OrderValidationError({
                    message: err.message,
                    order,
                    providerOrder: lifiTransaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CheckOrderIsValid'));
            traceMessage('Creating TX');
            this._tracer?.traceTime('CreateTx');
            const createTxPayload = {
                assetId: fromAsset.id,
                txData: {
                    chainId: transactionRequest.chainId,
                    data: transactionRequest.data,
                    from: transactionRequest.from,
                    gasLimit: quote.exchangeFeeData.feeOpts.gasLimit,
                    gasPrice: quote.exchangeFeeData.feeOpts?.gasPrice,
                    tipGasPrice: quote.exchangeFeeData.feeOpts?.tipGasPrice,
                    to: transactionRequest.to,
                    value: transactionRequest.value,
                },
                isSendAll,
            };
            const transactionPromise = createTx(createTxPayload)
                .catch((err) => {
                throw new CreateTxError({
                    message: err.message,
                    details: createTxPayload,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CreateTx'));
            const [transaction] = await Promise.all([transactionPromise, orderCheckPromise]);
            return {
                transaction,
                orderId: order.id,
                fromAmount: swapMode === SwapMode.ExactIn
                    ? fromAmount
                    : fromAmount.mul(1 + slippage / 100 + EXACT_OUT_ALLOWANCE_TRESHOLD),
                approvalAddress,
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async executeExchange({ pairId, fromAmount, transaction, orderId, approvalAddress, getAllowance, approveTokens, sendTx, }) {
        try {
            const traceMessage = this._traceMessageScope('executeExchange');
            const { fromAsset, fromToken } = await this._getAssetsAndTokensFromPairId(pairId);
            this._tracer?.traceTime('GetAllowance');
            const allowance = await getAllowance({
                approvalAddress,
                assetId: fromAsset.id,
            })
                .catch((err) => {
                throw new GetAllowanceError({
                    message: err.message,
                    assetId: fromAsset.id,
                    approvalAddress,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('GetAllowance'));
            const transactions = [];
            if (allowance.lt(fromAmount) && fromToken.address !== ETH_NULL_ADDRESS) {
                if (allowance.isPositive && ZERO_ALLOWANCE_ASSETS.includes(fromAsset.id)) {
                    traceMessage('Revoking allowance');
                    this._tracer?.traceTime('ApproveTokens');
                    const revokeApproveTxId = await approveTokens({
                        assetId: fromAsset.id,
                        approveAmount: fromAsset.currency.ZERO,
                        spenderAddress: approvalAddress,
                    })
                        .catch((err) => {
                        throw new ApproveTokensError({
                            message: err.message,
                            assetId: fromAsset.id,
                            approvalAddress,
                            amount: fromAsset.currency.ZERO,
                        });
                    })
                        .finally(() => this._tracer?.traceTimeEnd('ApproveTokens'));
                    transactions.push({ type: 'preSetupTransaction', id: revokeApproveTxId });
                }
                traceMessage('Approving tokens');
                this._tracer?.traceTime('ApproveTokens');
                const approveTxId = await approveTokens({
                    assetId: fromAsset.id,
                    approveAmount: fromAmount,
                    spenderAddress: approvalAddress,
                })
                    .catch((err) => {
                    throw new ApproveTokensError({
                        message: err.message,
                        assetId: fromAsset.id,
                        approvalAddress,
                        amount: fromAmount,
                    });
                })
                    .finally(() => this._tracer?.traceTimeEnd('ApproveTokens'));
                transactions.push({ type: 'setupTransaction', id: approveTxId });
            }
            traceMessage('Sending TX');
            this._tracer?.traceTime('SendTx');
            const { txId } = await sendTx(transaction)
                .catch((err) => {
                throw new SendTxError({
                    message: err.message,
                    details: transaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('SendTx'));
            transactions.push({ type: 'swapTransaction', id: txId });
            traceMessage('Updating order with txId');
            this._ordersService
                .updateDex({
                id: orderId,
                transactionId: txId,
            })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
            return { transactions, providerOrderId: txId, svc: SVC };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async getLimits({ pairId }) {
        const { from, to } = deconstructPairId(pairId);
        const [{ fromAsset, toAsset }, usdPrices] = await Promise.all([
            this._getAssetsAndTokensFromPairId(pairId),
            this._pricingService.getPrices([from, to]),
        ]);
        const { min, max } = this._getUsdLimitAmountsForSwap({ fromAsset, toAsset });
        const fromPrice = usdPrices[fromAsset.id];
        const toPrice = usdPrices[toAsset.id];
        if (!fromPrice || !toPrice) {
            throw new Error(`Missing usd price for ${fromPrice ? to : from}`);
        }
        const exactInMinAmount = min / fromPrice;
        const exactInMaxAmount = max / fromPrice;
        const exactOutMinAmount = min / toPrice;
        const exactOutMaxAmount = max / toPrice;
        return [
            {
                [SwapMode.ExactIn]: {
                    min: fromAsset.currency.defaultUnit(exactInMinAmount),
                    max: fromAsset.currency.defaultUnit(exactInMaxAmount),
                },
                [SwapMode.ExactOut]: {
                    min: toAsset.currency.defaultUnit(exactOutMinAmount),
                    max: toAsset.currency.defaultUnit(exactOutMaxAmount),
                },
            },
        ];
    }
    setGeolocation(geolocation) {
        if (!geolocation) {
            this._apiKey = this._apiKeys?.default;
            this._integrator = this._integrators.default;
            return;
        }
        const geolocationKey = getGeolocationKey(geolocation);
        if (this._apiKeys?.hasOwnProperty(geolocationKey)) {
            this._apiKey = this._apiKeys[geolocationKey];
        }
        else {
            this._apiKey = this._apiKeys?.default;
        }
        if (this._integrators.hasOwnProperty(geolocationKey)) {
            this._integrator = this._integrators[geolocationKey];
        }
        else {
            this._integrator = this._integrators.default;
        }
    }
    _getUsdLimitAmountsForSwap = (params) => {
        const { fromAsset, toAsset } = params;
        const isXChain = fromAsset.network !== toAsset.network;
        if (isXChain) {
            const isFromEthereum = fromAsset.network === 'ethereum';
            return { min: isFromEthereum ? X_CHAIN_ETH_MIN_AMOUNT_USD : 50, max: MAX_AMOUNT_USD };
        }
        return { min: MIN_AMOUNT_USD, max: MAX_AMOUNT_USD };
    };
    _getQuoteFromLifi = async ({ amount, fromAddress, fromAsset, fromToken, slippage, toAddress, toAsset, toToken, includePartners = [], swapMode = SwapMode.ExactIn, }) => {
        const baseAmount = amount.toBaseString({ unit: false });
        const fromChain = `${NETWORK_TO_CHAIN_ID.get(fromAsset.network)}`;
        const toChain = `${NETWORK_TO_CHAIN_ID.get(toAsset.network)}`;
        const quoteParams = {
            fromAddress,
            fromChain,
            fromToken: fromToken.address,
            integrator: this._integrator,
            fee: this._fee,
            toChain,
            toToken: toToken.address,
            ...(swapMode === SwapMode.ExactIn ? { fromAmount: baseAmount } : { toAmount: baseAmount }),
            ...(toAddress ? { toAddress } : {}),
            ...(slippage ? { slippage: slippage / 100 } : {}),
            ...(includePartners.length > 0 ? { allowBridges: includePartners } : {}),
        };
        const response = (await this._request(swapMode === SwapMode.ExactIn ? 'quote' : 'quote/toAmount').get(quoteParams));
        if (swapMode === SwapMode.ExactOut) {
            const estimatedFromAmountWithSlippage = fromAsset.currency.baseUnit(response.estimate.fromAmount);
            const slippagePercentage = slippage / 100;
            const slippageEquivalent = estimatedFromAmountWithSlippage.mul(slippagePercentage);
            const fromAmountWithoutSlippage = estimatedFromAmountWithSlippage.sub(slippageEquivalent);
            response.estimate.fromAmount = fromAmountWithoutSlippage.toBaseString();
        }
        return response;
    };
    _fetchSupportedChainsByBridge = memoizeWithTimeout(async (_bridges) => {
        const { bridges } = (await this._request('tools').get());
        const allowedBridges = bridges.filter((bridge) => _bridges.includes(bridge.key));
        const allowedBridgesWithChains = allowedBridges.map((bridge) => ({
            ...bridge,
            supportedChains: bridge.supportedChains.filter(({ fromChainId, toChainId }) => CHAIN_ID_TO_NETWORKS.has(fromChainId) && CHAIN_ID_TO_NETWORKS.has(toChainId)),
        }));
        return new Map(allowedBridgesWithChains.map((bridge) => {
            const key = bridge.key;
            const chainByName = bridge.supportedChains.map((chain) => CHAIN_ID_TO_NETWORKS.get(chain.fromChainId));
            return [key, chainByName];
        }));
    }, (bridges) => bridges.join(','));
    _estimateGasLimit(lifiGasCosts) {
        return lifiGasCosts.reduce((acc, gasCost) => {
            const gasPrice = parseInt(gasCost.price);
            const estimate = parseInt(gasCost.amount);
            if (gasPrice > 0 && estimate > 0) {
                const estimatedLowGasLimit = estimate / gasPrice;
                const estimatedHighGasLimit = parseInt(gasCost.limit);
                const difference = estimatedHighGasLimit - estimatedLowGasLimit;
                const estimatedGasLimit = difference > 0
                    ? estimatedLowGasLimit + difference * (this._options.gasLimitMultiplier || 1)
                    : estimatedLowGasLimit;
                return Math.ceil(acc + estimatedGasLimit);
            }
            return acc;
        }, 0);
    }
    _getAssetsAndTokensFromPairId = memoizeWithTimeout(async (pairId) => {
        const { from, to } = deconstructPairId(pairId);
        const tokens = await this._getTokens();
        const fromToken = tokens.get(from);
        const toToken = tokens.get(to);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !fromToken || !toAsset || !toToken) {
            throw new Error('missing pair');
        }
        return {
            fromAsset,
            fromToken,
            toAsset,
            toToken,
        };
    });
    async _checkOrderIsValid({ lifiTransaction, order, }) {
        const { from, to } = deconstructPairId(order.pairId);
        const fromAsset = await this._assetsService.getOneById(from);
        const toAsset = await this._assetsService.getOneById(to);
        if (!fromAsset || !toAsset) {
            throw new Error('missing pair');
        }
        const fromTokenAddress = fromAsset.meta?.contractAddress || ETH_NULL_ADDRESS;
        const toTokenAddress = toAsset.meta?.contractAddress || ETH_NULL_ADDRESS;
        const depositSwapData = lifiTransaction.swapData[0];
        const receivingSwapData = lifiTransaction.swapData[lifiTransaction.swapData.length - 1];
        if (!depositSwapData || !receivingSwapData) {
            throw new Error('Invalid Order: no swap data');
        }
        const fromAmount = fromAsset.currency.baseUnit(depositSwapData.fromAmount);
        const fromAmountWithoutSlippage = order.swapMode === SwapMode.ExactOut ? fromAmount.mul(1 - order.slippage / 100) : fromAmount;
        const amountsValid = areAmountsValid(fromAmountWithoutSlippage.toDefaultString(), String(order.amount.value));
        if (!amountsValid) {
            throw new Error(`Invalid Order: ${fromAmount.toDefaultString()} !== ${order.amount.value}`);
        }
        if (lifiTransaction.receiver.toLowerCase() !== order.toAddress.toLowerCase()) {
            throw new Error(`Invalid Order: ${lifiTransaction.receiver.toLowerCase()} !== ${order.toAddress.toLowerCase()}`);
        }
        if (depositSwapData.sendingAssetId.toLowerCase() !== fromTokenAddress.toLowerCase()) {
            throw new Error(`Invalid Order: ${depositSwapData.sendingAssetId.toLowerCase()} !== ${fromTokenAddress.toLowerCase()}`);
        }
        if (fromAsset.network === toAsset.network &&
            receivingSwapData.receivingAssetId.toLowerCase() !== toTokenAddress.toLowerCase()) {
            throw new Error(`Invalid Order: ${receivingSwapData.receivingAssetId.toLowerCase()} !== ${toTokenAddress.toLowerCase()}`);
        }
    }
    _getTokens = memoizeWithTimeout(async () => {
        const assetsByNetworks = await this._assetsService.getAllByNetworks([
            ...CHAIN_ID_TO_NETWORKS.values(),
        ]);
        const { tokens } = await this._request('tokens').get({
            chains: [...CHAIN_ID_TO_NETWORKS.keys()],
        });
        const tokensByAssetId = new Map();
        assetsByNetworks.forEach((assetById, network) => {
            const assets = [...assetById.values()];
            const assetByAddress = new Map(assets.map((asset) => {
                const assetAddress = asset[network]?.contractAddress?.toLowerCase();
                return [assetAddress, asset];
            }));
            const liFiTokensInNetwork = tokens[NETWORK_TO_CHAIN_ID.get(network) || ''];
            liFiTokensInNetwork?.forEach((token) => {
                const asset = assetByAddress.get(token.address.toLowerCase());
                if (!asset) {
                    return;
                }
                tokensByAssetId.set(asset.id, token);
            });
            const baseToken = liFiTokensInNetwork?.find((token) => token.address === ETH_NULL_ADDRESS);
            const baseAsset = [...(assetsByNetworks.get(network)?.values() || [])].find((asset) => baseToken.symbol === 'POL' ? asset?.symbol === 'MATIC' : asset?.symbol === baseToken.symbol);
            if (baseAsset) {
                tokensByAssetId.set(baseAsset.id, baseToken);
            }
        });
        return tokensByAssetId;
    });
    _request(subpath) {
        const headers = {
            ...(this._apiKey ? { 'x-lifi-api-key': this._apiKey } : {}),
        };
        const uri = new URL(`${this._baseUrl}/${subpath}`);
        return this._fetchival(uri, { headers });
    }
    _traceMessageScope(scope) {
        return (message) => this._tracer?.traceMessage(`${this.name}:${scope}:${message}`);
    }
}
export default LiFi;
